import React from 'react';
import { TicketIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { AGE_RESTRICTIONS } from '../../../data/eventSchema';
import { CURRENCIES } from './constants';
import type { EM } from './useEventManager';
export const AccessRulesSection: React.FC<{
  em: EM;
}> = ({
  em
}) => {
  const dk = useDarkClasses();
  const {
    formData,
    updateNestedField,
    addTicketTier,
    updateTicketTier,
    removeTicketTier
  } = em;
  const rules = formData.accessRules!;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-18460" data-mp="355u5cn2li49q">
      <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-18461" data-mp="39y3rrt4i4m79">
        <TicketIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-18462" data-mp="3exq5e3o8s4gl" />
        Attendance & Access Rules
      </h2>

      <div className="space-y-6" data-id="element-18463" data-mp="3jxm6nv5k1hsg">
        {/* Ticketing */}
        <div data-id="element-18464" data-mp="3ovnsuoya1x85">
          <div className="flex items-center justify-between mb-4" data-id="element-18465" data-mp="3ov4j3vis6f57">
            <label className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18466" data-mp="3ot9xsg3lkl0l">
              Is a ticket required for entry?
            </label>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-18467" data-mp="3mntnmwzm873h">
              <input type="checkbox" className="sr-only peer" checked={rules.ticketRequired} onChange={e => updateNestedField('accessRules', 'ticketRequired', e.target.checked)} data-id="element-18468" data-mp="3hoqgn702rqsv" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600" data-id="element-18469" data-mp="9bj6wpkt8qsz"></div>
            </label>
          </div>

          {rules.ticketRequired && <div className="space-y-4 pl-4 border-l-2 border-purple-200 dark:border-purple-900" data-id="element-18470" data-mp="2rdoenkgew8wm">
              <div data-id="element-18471" data-mp="2cpbr9bve3ydq">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18472" data-mp="2cptks0qo3yhy">
                  Ticket Link
                </label>
                <input type="url" value={rules.ticketLink || ''} onChange={e => updateNestedField('accessRules', 'ticketLink', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="https://eventbrite.com/..." data-id="element-18473" data-mp="23olzqkd5wu5t" />
              </div>

              <div data-id="element-18474" data-mp="3t3200vxgfatp">
                <div className="flex items-center justify-between mb-2" data-id="element-18475" data-mp="3t3jzs2jbs76d">
                  <label className={`block text-sm font-medium ${dk.textSecondary}`} data-id="element-18476" data-mp="1hhrot1nfj4ay">
                    Ticket Tiers (Optional)
                  </label>
                  <button type="button" onClick={addTicketTier} className={`text-xs font-medium flex items-center text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300`} data-id="element-18477" data-mp="1r7kyfx40h9ck">
                    <PlusIcon className="w-3 h-3 mr-1" data-id="element-18478" data-mp="1w6nr0i2t1n3n" /> Add Tier
                  </button>
                </div>

                <div className="space-y-3" data-id="element-18479" data-mp="2a4m1vdhx31ee">
                  {rules.ticketTiers.map((tier, index) => <div key={tier.id} className={`p-3 rounded-lg border ${dk.border} ${dk.surfaceMuted}`} data-id="element-18480" data-mp="257vpb05bmnxq" data-mpi={index}>
                      <div className="flex justify-between items-start mb-2" data-id="element-18481" data-mp="atcod20fgsht">
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-18482" data-mp="c9ytnnkor6xj">
                          Tier {index + 1}
                        </h4>
                        <button type="button" onClick={() => removeTicketTier(tier.id)} className="text-red-500 hover:text-red-700" data-id="element-18483" data-mp="m8xskefnfo7c">
                          <TrashIcon className="w-4 h-4" data-id="element-18484" data-mp="3pmidrq9mxlj4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3" data-id="element-18485" data-mp="3ulda7v0iz8lp">
                        <div className="sm:col-span-6" data-id="element-18486" data-mp="3fbtb5tvpks5">
                          <input type="text" value={tier.name} onChange={e => updateTicketTier(tier.id, 'name', e.target.value)} placeholder="e.g. VIP Early Access" className={`w-full p-2 text-sm rounded-md border ${dk.inputOnCard}`} data-id="element-18487" data-mp="896j8r02uvzo" />
                        </div>
                        <div className="sm:col-span-3" data-id="element-18488" data-mp="1k0mc5laws6b2">
                          <input type="number" value={tier.price || ''} onChange={e => updateTicketTier(tier.id, 'price', parseFloat(e.target.value))} placeholder="Price" className={`w-full p-2 text-sm rounded-md border ${dk.inputOnCard}`} data-id="element-18489" data-mp="1k38l9pdg6tve" />
                        </div>
                        <div className="sm:col-span-3" data-id="element-18490" data-mp="md57xhs3sxpy">
                          <select value={tier.currency} onChange={e => updateTicketTier(tier.id, 'currency', e.target.value)} className={`w-full p-2 text-sm rounded-md border ${dk.inputOnCard}`} data-id="element-18491" data-mp="mfqciejsdrre">
                            {CURRENCIES.map(c => <option key={c} value={c} data-id="element-18492" data-mp="10fvm6dgwirj6">
                                {c}
                              </option>)}
                          </select>
                        </div>
                      </div>
                      <input type="text" value={tier.description} onChange={e => updateTicketTier(tier.id, 'description', e.target.value)} placeholder="What's included? (e.g. 1hr early entry, swag bag)" className={`w-full p-2 text-sm rounded-md border ${dk.inputOnCard}`} data-id="element-18493" data-mp="qnpt10ewy42e" />
                    </div>)}
                  {rules.ticketTiers.length === 0 && <p className={`text-sm italic ${dk.textFaint}`} data-id="element-18494" data-mp="2kxwop938rmru">
                      No ticket tiers added. Add tiers to show pricing options
                      to readers.
                    </p>}
                </div>
              </div>
            </div>}
        </div>

        <hr className={dk.border} data-id="element-18495" data-mp="2pyu4jf0s44q6" />

        {/* Restrictions & Policies */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-18496" data-mp="a4ady7gya9oq">
          <div data-id="element-18497" data-mp="571s9wwlfniq">
            <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18498" data-mp="55gwk3yjlyja">
              Age Restriction
            </label>
            <select value={rules.ageRestriction} onChange={e => updateNestedField('accessRules', 'ageRestriction', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18499" data-mp="23obywttv7mf0">
              {AGE_RESTRICTIONS.map(age => <option key={age.value} value={age.value} data-id="element-18500" data-mp="2mejwkwespknt">
                  {age.label}
                </option>)}
            </select>
          </div>

          <div data-id="element-18501" data-mp="2repvmxnea9fx">
            <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18502" data-mp="2rc3o2y0i7txp">
              Book Limit Policy
            </label>
            <input type="text" value={rules.bookLimitPolicy || ''} onChange={e => updateNestedField('accessRules', 'bookLimitPolicy', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. 3 books per author, 1 outside book" data-id="element-18503" data-mp="x1gfin0cfpop" />
          </div>
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18504" data-mp="fse68q1250b8">
          {[{
          id: 'preorderRequired',
          label: 'Preorders Required'
        }, {
          id: 'outsideBooksAllowed',
          label: 'Outside Books Allowed'
        }, {
          id: 'photosAllowed',
          label: 'Photos Allowed'
        }, {
          id: 'personalisationAllowed',
          label: 'Personalisation Allowed'
        }].map(toggle => <div key={toggle.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-18505" data-mp="3j70el4pc3al3">
              <label className={`text-sm font-medium ${dk.textSecondary}`} data-id="element-18506" data-mp="1su2itroqqjnx">
                {toggle.label}
              </label>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-18507" data-mp="27jqhsm9g968h">
                <input type="checkbox" className="sr-only peer" checked={rules[toggle.id as keyof typeof rules] as boolean} onChange={e => updateNestedField('accessRules', toggle.id, e.target.checked)} data-id="element-18508" data-mp="22rgoqxd1fq1z" />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600" data-id="element-18509" data-mp="2ljrkm799ynql"></div>
              </label>
            </div>)}
        </div>
      </div>
    </div>;
};